import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

export type DebouncedSearchInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> & {
  value: string;
  onSearch: (value: string) => void;
  delay?: number;
  minLength?: number;
  shouldSearch?: (value: string) => boolean;
  searchOnMount?: boolean;
};

const DebouncedSearchInput = forwardRef<HTMLInputElement, DebouncedSearchInputProps>(({
  value,
  onSearch,
  delay = 250,
  minLength = 0,
  shouldSearch,
  searchOnMount = false,
  onChange,
  onKeyDown,
  ...inputProps
}, ref) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const canSearch = useCallback((query: string) => {
    if (!query) return true;
    return shouldSearch ? shouldSearch(query) : query.length >= minLength;
  }, [minLength, shouldSearch]);

  const searchNow = useCallback((nextValue: string, force = false) => {
    cancel();
    const query = nextValue.trim();
    if (!force && !canSearch(query)) return;
    onSearchRef.current(query);
  }, [canSearch, cancel]);

  const scheduleSearch = useCallback((nextValue: string) => {
    cancel();
    const query = nextValue.trim();
    if (!canSearch(query)) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onSearchRef.current(query);
    }, delay);
  }, [canSearch, cancel, delay]);

  const initialValueRef = useRef(value);
  const initialSearchTriggeredRef = useRef(false);
  useEffect(() => {
    return cancel;
  }, [cancel]);
  useEffect(() => {
    if (!searchOnMount || initialSearchTriggeredRef.current) return;
    initialSearchTriggeredRef.current = true;
    scheduleSearch(initialValueRef.current);
  }, [scheduleSearch, searchOnMount]);

  return <input
    {...inputProps}
    ref={ref}
    value={value}
    onChange={(event) => {
      onChange?.(event);
      scheduleSearch(event.target.value);
    }}
    onKeyDown={(event) => {
    onKeyDown?.(event);
      if (!event.defaultPrevented && event.key === 'Enter') {
        searchNow(event.currentTarget.value, true);
      }
    }}
  />;
});

DebouncedSearchInput.displayName = 'DebouncedSearchInput';

export default DebouncedSearchInput;
